import { functions } from "../../firebase";
import { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import ScrollToTop from "../ScrollToTop";
import PaymentChart from "./PaymentChart2";
import FraudSection from "./FraudSection";
import GeoChart from "./GeoChart";
import LatestPeriod from "./LatestPeriod";
import PaymentsSection from "./PaymentsSection";
import Loading from "./Loading";

import {
  Divider,
  Paper,
  Typography,
  ButtonGroup,
  Button,
  Grid,
  Box,
} from "@material-ui/core";
import { Refresh as RefreshIcon } from "@material-ui/icons";
import keys from "lodash/keys";
import BigChartRoyaltiesbyPlatform from "../BigChartRoyaltiesByPlatform";

// Cache configuration
const CACHE_KEY_PREFIX = "royalties_dashboard_";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const CACHE_VERSION = "1.1";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    width: "100%",
    maxWidth: "1100px",
    margin: "auto",
    marginTop: theme.spacing(15),
    marginBottom: theme.spacing(15),
    paddingLeft: 0,
    paddingRight: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(6),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(8),
      gap: theme.spacing(4),
    },
  },
  noData: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100vw",
    height: "100vh",
    paddingTop: "150px",
  },
  navigationButtons: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  buttonGroup: {},
  chartsContainer: {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
  },
  chartSection: {
    marginBottom: theme.spacing(12),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  refreshSection: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: theme.spacing(4),
    padding: theme.spacing(3),
  },
  refreshButton: {
    minWidth: "200px",
    padding: theme.spacing(1.5, 3),
  },
}));

const getReportingDashboard = functions.httpsCallable("getReportingDashboard");

// Cache utility functions
const getCacheKey = (query) => {
  return `${CACHE_KEY_PREFIX}${JSON.stringify(query)}_v${CACHE_VERSION}`;
};

const getCachedData = (query) => {
  try {
    const cacheKey = getCacheKey(query);
    const cached = localStorage.getItem(cacheKey);

    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();

    if (now - timestamp > CACHE_DURATION) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return data;
  } catch (error) {
    console.warn("Error reading from cache:", error);
    return null;
  }
};

const setCachedData = (query, data) => {
  try {
    const cacheKey = getCacheKey(query);
    // Exclude payment data from cache
    const { merged, multas, ...cacheableData } = data;
    const cacheData = {
      data: cacheableData,
      timestamp: Date.now(),
    };

    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.warn("Error writing to cache:", error);
    if (error.name === "QuotaExceededError") {
      clearOldCacheEntries();
    }
  }
};

const clearCacheForQuery = (query) => {
  try {
    const cacheKey = getCacheKey(query);
    localStorage.removeItem(cacheKey);
    console.log("Cache cleared for current query");
  } catch (error) {
    console.warn("Error clearing cache:", error);
  }
};

const clearOldCacheEntries = () => {
  try {
    const keys = Object.keys(localStorage);
    const cacheKeys = keys.filter((key) => key.startsWith(CACHE_KEY_PREFIX));

    const cacheEntries = cacheKeys
      .map((key) => {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          return { key, timestamp: data.timestamp || 0 };
        } catch {
          return { key, timestamp: 0 };
        }
      })
      .sort((a, b) => a.timestamp - b.timestamp);

    const toRemove = cacheEntries.slice(0, Math.floor(cacheEntries.length / 2));
    toRemove.forEach((entry) => localStorage.removeItem(entry.key));
  } catch (error) {
    console.warn("Error clearing old cache entries:", error);
  }
};

const Royalties = ({ query }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [bigChartData, setBigChartData] = useState([]);
  const [bigChartByPlatform, setBigChartByPlatform] = useState([]);
  const [fraudData, setFraudData] = useState([]);
  const [geoChartData, setGeoChartData] = useState([]);
  const [latest, setLatest] = useState({});
  const [noData, setNoData] = useState(false);
  const [reloadData, setReloadData] = useState(0);
  const [dataUID, setDataUID] = useState(null);
  const [hasFraudData, setHasFraudData] = useState(false);

  // Shared pagination state for both charts
  const [sharedPageSize, setSharedPageSize] = useState(6);
  const [sharedPage, setSharedPage] = useState(1);

  const loadDataFromServer = async (showRefreshLoader = false) => {
    if (!query) return;

    if (showRefreshLoader) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      console.log("Loading dashboard data from server");
      const { data } = await getReportingDashboard(query);

      const {
        uid,
        bigChart,
        bigChartByPlatform,
        fraudChart,
        geoChart,
        latest,
        fraudDonut,
        fraud,
        error,
      } = data;

      if (error || !data) {
        setNoData(true);
      } else {
        // Cache the successful response (excluding payment data)
        setCachedData(query, data);
        populateStateFromData(data);
        setNoData(false);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      setNoData(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (!query) return;

      setLoading(true);

      try {
        // Try to get cached data first
        const cachedData = getCachedData(query);
        if (cachedData) {
          console.log("Loading dashboard data from cache");
          populateStateFromData(cachedData);
          setLoading(false); // Hide loading immediately when using cache
          return;
        }

        // If no cache, fetch from server

        await loadDataFromServer();
      } catch (error) {
        console.error("Error loading data:", error);
        setNoData(true);
        setLoading(false);
      }
    };

    loadData();
  }, [query, reloadData]);

  const populateStateFromData = (data) => {
    const {
      uid,
      bigChart,
      bigChartByPlatform,
      fraudChart,
      geoChart,
      latest,
      fraudDonut,
      fraud,
    } = data;

    setBigChartData(bigChart || []);
    setBigChartByPlatform(bigChartByPlatform || []);
    setFraudData(fraud || {}); // Change to object, not array
    setGeoChartData(geoChart || []);
    setLatest(latest || {});
    setDataUID(uid);

    // Check if fraud data exists and is valid
    const fraudDataExists =
      fraud &&
      typeof fraud === "object" &&
      ((fraud.fraudulentTracks && fraud.fraudulentTracks.length > 0) ||
        (fraud.fraudulentByPeriod && fraud.fraudulentByPeriod.length > 0) ||
        (fraud.fraudLight && fraud.fraudLight.length > 0));

    setHasFraudData(fraudDataExists);
  };

  const handleRefreshData = async () => {
    if (!query) return;

    // Clear cache for current query
    clearCacheForQuery(query);

    // Load fresh data from server
    await loadDataFromServer(true);
  };

  useEffect(() => {
    if (query) {
      setReloadData(reloadData + 1);
    }
  }, [query]);

  const handleScrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const margin = 50;
      const navbar = document.querySelector('[class*="MuiAppBar-root"]');
      const navbarHeight = navbar ? navbar.offsetHeight : 80;

      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight - margin;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const classes = useStyles();

  // Only show full-screen loader when actually loading from server
  if (loading) {
    return <Loading />;
  }

  if (noData && !loading) {
    return (
      <div className={classes.noData}>
        <Typography variant="button">Aún no hay datos para mostrar</Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <ScrollToTop />

      <LatestPeriod data={latest} />

      <div className={classes.navigationButtons}>
        <ButtonGroup
          variant="outlined"
          color="primary"
          className={classes.buttonGroup}
        >
          <Button onClick={() => handleScrollToSection("ingresos-section")}>
            Ingresos
          </Button>

          {hasFraudData && (
            <Button
              onClick={() => handleScrollToSection("fraudulento-section")}
            >
              Fraudulento
            </Button>
          )}
          <Button onClick={() => handleScrollToSection("pagos-section")}>
            Pagos
          </Button>
        </ButtonGroup>
      </div>

      <Paper
        elevation={2}
        className={classes.chartsContainer}
        id="ingresos-section"
      >
        <div className={classes.chartSection}>
          <PaymentChart
            data={bigChartData}
            sharedPageSize={sharedPageSize}
            sharedPage={sharedPage}
            onPageSizeChange={setSharedPageSize}
            onPageChange={setSharedPage}
            showPagination={true}
          />
        </div>

        <div className={classes.chartSection}>
          <BigChartRoyaltiesbyPlatform
            data={bigChartByPlatform}
            sharedPageSize={sharedPageSize}
            sharedPage={sharedPage}
            onPageSizeChange={setSharedPageSize}
            onPageChange={setSharedPage}
            showPagination={true}
          />
        </div>

        {/* {geoChartData && geoChartData.length > 0 && (
          <div className={classes.chartSection}>
            <GeoChart data={geoChartData} />
          </div>
        )} */}
      </Paper>

      {hasFraudData && (
        <div id="fraudulento-section" style={{ marginTop: "96px" }}>
          <FraudSection data={fraudData} />
        </div>
      )}

      <div id="pagos-section">
        <PaymentsSection query={query} uid={dataUID} />
      </div>

      <Box className={classes.refreshSection}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={handleRefreshData}
          disabled={refreshing}
          className={classes.refreshButton}
        >
          {refreshing ? "Actualizando..." : "Actualizar Reportes"}
        </Button>
      </Box>
    </div>
  );
};

export default Royalties;
